'use client';

import { DataTable } from '@/components/data-table/data-table';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDataTable } from '@/hooks/use-data-table';
import { CustomerDetail } from '@/lib/api/customers';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from "@/lib/money";
import { ColumnDef } from '@tanstack/react-table';
import { SubscriptionResourceBadge } from '../../../subscriptions/components/SubscriptionResourceBadge';
import { useTranslation } from 'react-i18next';

interface CustomerSubscriptionsProps {
  customer: CustomerDetail;
}

export function CustomerSubscriptions({ customer }: CustomerSubscriptionsProps) {
  const { t } = useTranslation('customers');
  // Transform customer subscriptions data to match the expected format
  const subscriptions =
    customer.subscriptions?.map((sub) => {
      const price = (sub as any).price ?? (sub as any).priceSnapshot;
      return {
        id: sub.id,
        status: sub.status,
        currency: sub.currency,
        currentPeriodStart: sub.currentPeriodStart,
        currentPeriodEnd: sub.currentPeriodEnd,
        nextBillingDate: sub.nextBillingDate,
        cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
        trialEnd: sub.trialEnd,
        quantity: sub.quantity,
        createdAt: sub.createdAt,
        customerId: customer.id,
        customerEmail: customer.email,
        customer: `${customer.firstName} ${customer.lastName}`.trim() || customer.email || '',
        storeId: customer.storeId || '',
        storeName: customer.store?.name || '',
        priceRecurring: price?.recurring || null,
        priceCurrencyOptions: price?.currencyOptions || null,
        priceDefault: price?.default || null,
        priceInterval: price?.interval || null,
        priceIntervalCount: price?.intervalCount || null,
        useTagadaVault: sub.useTagadaVault,
        processorName: null,
        processorId: null,
      };
    }) || [];

  const columns: ColumnDef<any>[] = [
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('subscriptions.columns.subscriptionId')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        return <SubscriptionResourceBadge subscriptionId={subscription.id} />;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('subscriptions.columns.status')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const statusColor =
          subscription.status === 'active'
            ? 'bg-green-100 text-green-800'
            : subscription.status === 'trialing'
              ? 'bg-blue-100 text-blue-800'
              : subscription.status === 'canceled'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800';
        return (
          <Badge variant="secondary" className={`text-xs ${statusColor}`}>
            {subscription.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
          </Badge>
        );
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'amount',
      accessorKey: 'priceDefault',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('subscriptions.columns.amount')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const amount = subscription.priceDefault?.amount || 0;
        const currency = subscription.currency || 'USD';
        return <div className="text-sm font-medium">{formatMoney(amount, currency)}</div>;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'interval',
      accessorKey: 'priceInterval',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('subscriptions.columns.interval')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const intervalCount = subscription.priceIntervalCount || 1;
        const interval = subscription.priceInterval || 'month';
        return (
          <div className="text-sm">
            {intervalCount} {interval}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'nextBillingDate',
      accessorKey: 'nextBillingDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('subscriptions.columns.nextBilling')} />,
      cell: ({ row }) => (
        <DateDisplay
          value={row.original.nextBillingDate}
          format="MM/dd/yyyy"
          className="text-sm"
          fallback="-"
        />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('subscriptions.columns.createdAt')} />,
      cell: ({ row }) => (
        <DateDisplay
          value={row.original.createdAt}
          format="MM/dd/yyyy"
          className="text-sm"
          fallback="-"
        />
      ),
      enableSorting: true,
      enableHiding: true,
    },
  ];

  const { table } = useDataTable({
    data: subscriptions,
    columns,
    total: subscriptions.length,
    config: { clientMode: true, defaultSorting: [{ id: 'createdAt', desc: true }] },
    onTableStateChange: () => {},
    getRowId: (row: any) => row.id,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('subscriptions.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center">
              {t('subscriptions.empty')}
            </div>
          ) : (
            <DataTable
              table={table}
              isLoading={false}
              showFilters={false}
              showToolbar={false}
              showPagination={true}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
